<template>
  <div class="tw:relative tw:max-w-[530px]" v-click-outside="clickAway">
    <div
      :class="getClasses"
      class="tw:max-w-[530px] tw:flex tw:items-center tw:justify-between tw:bg-white tw:rounded-lg tw:p-2"
    >
      <button
        data-testid="choose-engine-from-list-button"
        class="tw:py-1 tw:px-2 tw:w-full tw:flex tw:items-center"
        @click="showPartners"
      >
        <div class="tw:flex tw:flex-col tw:w-full tw:mr-2 tw:items-start">
          <p class="tw:text-gray-600 tw:text-xs">{{ $t('rc_menu_booking_engine_header') }}</p>

          <p class="tw:text-sm tw:font-bold">
            {{
              selectedOption === null
                ? $t('rc_menu_booking_engine_prompt')
                : selectedOption.isPartner
                  ? selectedOption.label
                  : $t(selectedOption.label)
            }}
          </p>
        </div>
        <ChevronRight
          v-if="partners.length > 1"
          class="tw:hover:cursor-pointer tw:size-6"
          :class="isOpen && partners.length > 0 && screenWidth > 767 ? 'tw:rotate-270' : 'tw:rotate-90'"
        />
      </button>
      <div class="tw:flex tw:pt-2 tw:lg:pt-0 tw:w-full tw:lg:w-auto">
        <MainButton
          @click="confirmSelection"
          class="tw:w-full tw:lg:w-auto"
          :disabled="selectedOption === null"
          dataTestId="select-eligible-booking-engine-button"
        >
          <span class="tw:text-nowrap">{{ $t('rc_booking_engine_select') }}</span>
        </MainButton>
      </div>
    </div>
    <div
      v-show="isOpen && partners.length > 1 && screenWidth > 767"
      class="tw:absolute tw:left-0 tw:bg-white shadow tw:w-full tw:top-full tw:mt-1 tw:rounded-lg tw:flex tw:flex-col tw:z-10"
      data-testid="choose-engine-from-list-dropdown"
    >
      <div class="tw:overflow-y-auto tw:max-h-[160px]" data-testid="choose-engine-list">
        <ul class="tw:my-3">
          <li v-for="(partner, index) in partners" :key="partner.value">
            <button
              :data-testid="`choose-engine-list-item-${index + 1}`"
              @click="selectOption(partner)"
              :class="
                partner.value === selectedOption?.value
                  ? 'tw:bg-gray-200 tw:hover:cursor-default'
                  : 'tw:hover:cursor-pointer'
              "
              class="tw:w-full tw:py-3 tw:px-[22px] tw:flex tw:flex-row tw:items-center tw:gap-x-3 tw:hover:bg-gray-200 tw:flex tw:items-center"
            >
              <PartnerIcon />
              <div class="tw:flex tw:flex-col tw:items-start">
                <span class="tw:gray-900" :data-testid="`partner-name-${index + 1}`">{{ partner?.label }}</span>
                <span class="tw:text-sm tw:text-gray-600">{{ $t('rc_smartboost_provider_supported') }}</span>
              </div>
            </button>
          </li>
        </ul>
      </div>

      <div class="tw:border-t tw:[box-shadow:0_-10px_15px_-10px_rgba(0,0,0,0.1)] tw:mb-3">
        <button
          v-for="(option, index) in footerOptions"
          :key="index"
          @click="selectOption(option)"
          class="tw:w-full tw:py-3 tw:px-[22px] tw:hover:bg-gray-200 tw:flex tw:items-center tw:gap-x-3 tw:cursor-pointer"
          :class="
            option.value === selectedOption?.value
              ? 'tw:bg-gray-200 tw:hover:cursor-default'
              : 'tw:hover:cursor-pointer'
          "
          :data-testid="`choose-engine-footer-option-${option.value}-button`"
        >
          <span class="tw:font-bold tw:text-blue-700">{{ $t(option.label) }}</span>
        </button>
      </div>
    </div>

    <div
      v-if="isOpen && partners.length > 0 && screenWidth < 1024"
      class="tw:fixed tw:inset-0 tw:top-0 tw:w-full tw:h-full tw:overflow-hidden tw:flex tw:overflow-y-auto tw:z-100"
    >
      <div class="tw:mx-auto tw:w-full tw:max-w-[980px] tw:bg-white tw:m-auto box-shadow tw:relative">
        <div
          class="tw:fixed tw:z-50 tw:w-full tw:h-[72px] tw:bg-white tw:flex tw:flex-row tw:justify-between tw:items-center tw:py-3.5 tw:px-8 tw:text-gray-800"
        >
          <div>
            <button class="tw:outline-hidden tw:pr-2 tw:pt-2" @click="clickAway" @keydown.enter="clickAway">
              <Close class="tw:size-2.5" />
            </button>
          </div>
          <div class="tw:font-bold">{{ $t('rc_opportunities_commission_cta') }}</div>
          <div />
        </div>

        <div class="tw:flex tw:flex-col tw:pb-[76px] tw:pt-[72px] tw:min-h-screen tw:p-5">
          <p class="tw:font-bold">{{ $t('connectivity_provider_info') }}</p>
          <ul class="tw:my-3">
            <li v-for="partner in partners" :key="partner.value">
              <button
                @click="selectOption(partner)"
                class="tw:w-full tw:py-3 tw:px-[22px] tw:hover:bg-gray-200 tw:flex tw:items-center tw:gap-x-3"
                :class="
                  partner.value === selectedOption?.value
                    ? 'tw:bg-gray-200 tw:hover:cursor-default'
                    : 'tw:hover:cursor-pointer'
                "
              >
                <PartnerIcon />

                <div class="tw:flex tw:flex-col">
                  <span class="tw:gray-900">{{ partner?.label }}</span>
                  <span class="tw:text-sm tw:text-gray-600">{{ $t('rc_smartboost_provider_supported') }}</span>
                </div>
              </button>
            </li>
          </ul>
        </div>

        <div
          class="shadow tw:fixed tw:bottom-0 tw:w-full tw:flex tw:flex-col tw:justify-between tw:items-center tw:py-4 tw:px-4 tw:text-gray-800 tw:bg-white"
        >
          <div class="tw:mb-3 tw:w-full">
            <div v-for="(option, index) in footerOptions" :key="index">
              <button
                @click="selectOption(option)"
                class="tw:w-full tw:py-3 tw:hover:bg-gray-200 tw:flex tw:items-center tw:gap-x-3 tw:cursor-pointer"
                :class="
                  option.value === selectedOption?.value
                    ? 'tw:bg-gray-200 tw:hover:cursor-default'
                    : 'tw:hover:cursor-pointer'
                "
              >
                <span class="tw:font-bold tw:text-blue-700 tw:px-4">{{ $t(option.label) }}</span>
              </button>
            </div>
          </div>

          <div class="tw:md:flex tw:lg:hidden tw:w-full tw:px-4">
            <MainButton class="tw:w-full" @click="confirmSelection" :disabled="selectedOption === null">
              <span class="tw:text-nowrap">{{ $t('rc_booking_engine_select') }}</span>
            </MainButton>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script setup>
import { computed, ref } from 'vue'
import { useRouter } from 'vue-router'

import MainButton from '@/components/base/buttons/MainButton.vue'
import ChevronRight from '@/components/icons/ChevronRight.svg?component'
import PartnerIcon from '@/components/icons/PartnerIcon.svg?component'
import Close from '@/components/icons/Close.svg?component'

import { useIsMobile } from '@/composables/useIsMobile'
import { selectedAccommodationId } from '@/layouts/queries'

import appAnalytics from '@/utils/tracking'

const router = useRouter()
const isOpen = ref(false)
const props = defineProps({
  partners: Array,
})

const footerOptions = ref([
  { label: 'rc_menu_booking_engine_not_listed', value: 'not-listed-partner', isPartner: false },
  { label: 'rc_menu_no_booking_engine', value: 'no-booking-engine', isPartner: false },
])
const selectedOption = ref(null)
const { screenWidth } = useIsMobile()
const selectOption = option => {
  selectedOption.value = option
  if (screenWidth.value > 767) {
    isOpen.value = false
  }
}

const confirmSelection = () => {
  if (selectedOption.value === null) return
  if (selectedOption.value?.isPartner) {
    appAnalytics.track(appAnalytics.events.RC_NOT_ELIGIBLE_BOOKING_ENGINE_CONNECTED, {
      item_id: selectedAccommodationId.value,
      partner_name: selectedOption.value.label,
      partner_id: selectedOption.value.value,
    })
    router.push({
      name: 'rate-connect-not-eligible-read-more',
      state: { partner: selectedOption.value.label },
    })
  } else {
    if (selectedOption.value?.value === 'no-booking-engine') {
      appAnalytics.track(appAnalytics.events.RC_NOT_ELIGIBLE_NO_BOOKING_ENGINE, {
        item_id: selectedAccommodationId.value,
      })
      router.push({
        name: 'rate-connect-not-eligible-read-more',
        state: { value: 'no-booking-engine' },
      })
    }
    if (selectedOption.value?.value === 'not-listed-partner') {
      appAnalytics.track(appAnalytics.events.RC_NOT_ELIGIBLE_NOT_LISTED_PARTNER, {
        item_id: selectedAccommodationId.value,
      })
      router.push({
        name: 'rate-connect-not-eligible-read-more',
        state: { value: 'not-listed-partner' },
      })
    }
  }
}

const showPartners = () => {
  isOpen.value = !isOpen.value
}
const clickAway = () => {
  isOpen.value = false
}

const getClasses = computed(() => {
  const classes = []
  classes.push('shadow tw:mr-0 tw:md:mr-6 tw:lg:mr-0 tw:flex-col tw:lg:flex-row tw:lg:max-w-[590px] tw:mb-12')
  if (props.partners.length > 1) {
    classes.push('tw:hover:cursor-pointer')
  }

  return classes.join(' ')
})
</script>
